import { BreakpointObserver, Breakpoints, BreakpointState } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { MatDrawer, MatDrawerContent } from "@angular/material/sidenav";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MenuItem } from "@vp/models";
import { LoggerService, ON_INIT } from "@vp/shared/logger-service";
import { PermissionsConstService } from "@vp/shared/permissions-const";
import { MenuMode, PageMode } from "@vp/shared/side-nav";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, tap } from "rxjs/operators";

@Component({
  selector: "vp-administration-nav",
  standalone: false,
  templateUrl: "./administration-nav.component.html",
  styleUrls: ["./administration-nav.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrationNavComponent implements OnInit, OnDestroy {
  private static readonly NAME = "AdministrationNavComponent";
  private destroyed$ = new Subject<void>();
  private readonly _opened$ = new BehaviorSubject<boolean>(true);
  private readonly _showToggle$ = new BehaviorSubject<boolean>(true);
  private readonly _menuMode$ = new BehaviorSubject<MenuMode>("side");
  private readonly _pageMode$ = new BehaviorSubject<PageMode>("desktop");
  private menuItems: MenuItem[] = [];
  private readonly ROUTE_DATA_BREADCRUMB = "breadcrumb";

  private mobile = [
    Breakpoints.XSmall,
    Breakpoints.Small,
    Breakpoints.TabletPortrait,
    Breakpoints.WebPortrait
  ];

  private desktop = [
    Breakpoints.Medium,
    Breakpoints.Large,
    Breakpoints.XLarge,
    Breakpoints.WebLandscape,
    Breakpoints.TabletLandscape
  ];

  pageMode = this._pageMode$.asObservable();
  menuMode = this._menuMode$.asObservable();
  opened = this._opened$.asObservable();
  showToggle = this._showToggle$.asObservable();

  @Output() readonly menuItemClickHandler = new EventEmitter<MenuItem>();
  @ViewChild("drawer") public drawer!: MatDrawer;
  @ViewChild("content") public content!: MatDrawerContent;

  constructor(
    private readonly logger: LoggerService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breakpointObserver: BreakpointObserver,
    public permConst: PermissionsConstService
  ) {}

  ngOnInit(): void {
    this.breakpointObserver
      .observe(this.mobile)
      .pipe(filter((state: BreakpointState) => state.matches))
      .subscribe(() => {
        this._opened$.next(false);
        this._showToggle$.next(true);
        this._menuMode$.next("over");
        this._pageMode$.next("mobile");
      });

    this.breakpointObserver
      .observe(this.desktop)
      .pipe(filter((state: BreakpointState) => state.matches))
      .subscribe(() => {
        this._opened$.next(true);
        this._showToggle$.next(false);
        this._menuMode$.next("side");
        this._pageMode$.next("desktop");
      });

    this.router.events
      .pipe(
        filter(event => {
          return event instanceof NavigationEnd;
        }),
        tap(() => {
          this.menuItems = this.createBreadcrumbs(this.activatedRoute.root);
        })
      )
      .subscribe({
        error: error => {
          this.logger.errorEvent(
            error,
            `${AdministrationNavComponent.NAME}.${ON_INIT}.router.events`
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  toggleSideNav() {
    this._opened$.next(!this._opened$.getValue());
    this.drawer?.toggle(this._opened$.getValue());
  }

  navigationItemClicked = (item: MenuItem) => {
    if (!item.disabled) {
      for (const index in this.menuItems) {
        if (
          Object.prototype.hasOwnProperty.call(this.menuItems, index) &&
          Object.prototype.hasOwnProperty.call(this.menuItems[index], "active") &&
          Object.prototype.hasOwnProperty.call(this.menuItems[index], "key")
        ) {
          this.menuItems[index].disabled = this.menuItems[index].id !== item.id;
        }
      }
      if (item.routerLink && this.breakpointObserver.isMatched(this.mobile)) {
        this._opened$.next(false);
      } else {
        this.menuItemClickHandler.emit(item);
      }
    }
  };

  private createBreadcrumbs(
    route: ActivatedRoute,
    url = "",
    breadcrumbs: MenuItem[] = []
  ): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join("/");
      if (routeURL !== "") {
        url += `/${routeURL}`;
      }

      if (child.snapshot.data[this.ROUTE_DATA_BREADCRUMB]) {
        const label = child.snapshot.data[this.ROUTE_DATA_BREADCRUMB];
        if (label) {
          breadcrumbs.push({ label, url });
        }
      }
      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }
}
